import React, { memo, useCallback, useState } from "react";

import {
  DrawerBody,
  DrawerHeader,
  DrawerContent,
  DrawerCloseButton,
  Center,
  Spinner,
  Text,
  Button,
  Tabs,
  TabList,
  Tab,
  Stack,
  Box,
} from "@chakra-ui/react";
import { useQuery } from "@apollo/client";
import { useAuth } from "modules/providers/auth-provider";
import colors from "app/colors";
import { DownloadIcon } from "modules/icons";
import {
  MessageThreadFilesDrawerFragment,
  MessageThreadFilesDrawerQuery,
  MessageThreadFilesDrawerQueryVariables,
} from "gql/graphql";
import MESSAGE_THREAD_FILES_DRAWER_QUERY from "./_networking/message-thread-files-drawer";
import MessageThreadMeRemovedHeader from "modules/messaging/_shared/message-thread-me-removed-header";
import { useOverlay } from "modules/providers/overlay-provider";
import { MODAL_TYPE } from "modules/providers/overlay-provider/overlay-provider";
import FilesDrawerBody from "./files-drawer-body";
import useMessageThreadFilesDrawer from "modules/hooks/use-message-thread-files-drawer";
import { MESSAGE_THREAD_FILES_DRAWER_TAB_TYPES } from "modules/utils/Constants";

interface IProps {
  messageThreadId: string;
}

const MessageThreadFilesDrawer = ({ messageThreadId }: IProps) => {
  const { me } = useAuth();
  const { openModal } = useOverlay();
  const [tabType, setTabType] = useState(
    MESSAGE_THREAD_FILES_DRAWER_TAB_TYPES[0],
  );

  const { data, error } = useQuery<
    MessageThreadFilesDrawerQuery,
    MessageThreadFilesDrawerQueryVariables
  >(MESSAGE_THREAD_FILES_DRAWER_QUERY, {
    variables: {
      id: messageThreadId,
    },
  });

  const messageThread: MessageThreadFilesDrawerFragment | undefined | null =
    data?.messageThread;

  const { getFiles } = useMessageThreadFilesDrawer(messageThread);
  const links = messageThread?.linkList || [];

  const onDownloadAllClicked = useCallback(() => {
    openModal({
      type: MODAL_TYPE.DOWNLOAD_ALL_FILES,
      id: messageThreadId,
    });
  }, [messageThreadId, openModal]);

  const onTabChange = useCallback((index: number) => {
    setTabType(MESSAGE_THREAD_FILES_DRAWER_TAB_TYPES[index]);
  }, []);

  if (!messageThread || !me) {
    return (
      <Center h="100%">
        <Spinner />
      </Center>
    );
  }

  if (error) {
    return <Text>{error.message}</Text>;
  }

  return (
    <DrawerContent>
      <DrawerCloseButton
        right={0}
        left={3}
        top={0}
        size="lg"
        color={colors.gray30}
      />
      <DrawerHeader py={1} px={0}>
        <Stack spacing={1}>
          <Center fontWeight="bold">
            Attachments & Links (
            {messageThread.fileCount + messageThread.linkList.length})
          </Center>
          {messageThread.fileList.length > 0 && (
            <Center>
              <Button
                aria-label="Download Attachments Zip"
                variant="ghost"
                color={colors.blue}
                fontSize={16}
                fontWeight={700}
                onClick={onDownloadAllClicked}
                leftIcon={<DownloadIcon boxSize={5} fill={colors.blue} />}
              >
                Download Attachments (ZIP)
              </Button>
            </Center>
          )}
          <Box px={4}>
            <MessageThreadMeRemovedHeader messageThread={messageThread} />
          </Box>
          <Tabs w="full" onChange={onTabChange}>
            <TabList>
              {MESSAGE_THREAD_FILES_DRAWER_TAB_TYPES.map((tab) => (
                <Tab
                  key={tab}
                  w={`${(1 / MESSAGE_THREAD_FILES_DRAWER_TAB_TYPES.length) * 100}%`}
                  _selected={{
                    color: colors.orangePrimary,
                    fontWeight: "semibold",
                    borderBottomColor: colors.orangePrimary,
                    borderBottomWidth: 2,
                  }}
                  color={colors.grayMain}
                  textTransform="uppercase"
                  fontSize="sm"
                  pt={0}
                >
                  {tab}
                  {getFiles(tab).length > 0 && ` (${getFiles(tab).length})`}
                  {tab === "LINKS" && links.length > 0 && ` (${links.length})`}
                </Tab>
              ))}
            </TabList>
          </Tabs>
        </Stack>
      </DrawerHeader>
      <DrawerBody p={0}>
        <FilesDrawerBody messageThread={messageThread} tabType={tabType} />
      </DrawerBody>
    </DrawerContent>
  );
};

export default memo(MessageThreadFilesDrawer);
